import { useUpdateEffect } from '@cian/react-utils';

import { useCallback, useEffect, useRef } from 'react';

import { useMapFeaturesContext } from '@/microfrontends/map/shared/context/MapLayersCenterContext';

import { type Cluster } from '@/packages/query-state/shared/types/clusters/ClustersResponse';
import { boundActionsFactory } from '@/packages/redux-state/store';
import { useActivePinIdSelector } from '@/packages/redux-state/store/slices/mapControls';

const useBoundActions = boundActionsFactory(reduxActions => ({
  setHoveredPinId: reduxActions.mapControls.setHoveredPinId,
  clearHoveredPinId: reduxActions.mapControls.clearHoveredPinId,
}));

/**
 * @description
 * Обработчики ховера на балунах
 */
export const useBalloonHoverHandlers = (): void => {
  const { balloonManager } = useMapFeaturesContext();

  const actions = useBoundActions();

  const activePinId = useActivePinIdSelector();
  const lastActivePinIdRef = useRef(activePinId);

  const handleBalloonMouseEnter = useCallback(
    (event: YMaps.IEvent) => {
      const ballon = balloonManager.objectManager.objects.getById<Cluster>(event.get<string>('objectId'));
      const lastActivePinId = lastActivePinIdRef.current;

      if (!ballon || lastActivePinId === ballon.id) {
        return;
      }

      actions.setHoveredPinId(ballon.id);
    },
    [actions, balloonManager],
  );

  const handleBalloonMouseLeave = useCallback(() => actions.clearHoveredPinId(), [actions]);

  useEffect(() => {
    balloonManager.objectManager.objects.events.add('mouseenter', handleBalloonMouseEnter);
    balloonManager.objectManager.objects.events.add('mouseleave', handleBalloonMouseLeave);

    return (): void => {
      balloonManager.objectManager.objects.events.remove('mouseenter', handleBalloonMouseEnter);
      balloonManager.objectManager.objects.events.remove('mouseleave', handleBalloonMouseLeave);
    };
  }, [balloonManager, handleBalloonMouseEnter, handleBalloonMouseLeave]);

  useUpdateEffect(() => {
    lastActivePinIdRef.current = activePinId;
  }, [activePinId]);
};
